"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import {
	appendGalleryLoadCandidatesForRawUrl,
	buildGalleryLoadCandidatesForItem,
	getServiceMediaOriginalSource,
	getServiceMediaRasterSource,
} from "@/lib/utils/service-media-image";

type Props = {
	/** Primary media item or a single raster URL string. */
	source: { displayUrl?: string | null; url?: string | null } | string;
	fallbackRasterSources?: string[];
	alt: string;
	className?: string;
	loading?: "eager" | "lazy";
	onClick?: () => void;
	/** Listing/card tiles — direct Media CloudFront when Tier A display URL exists. */
	listingCard?: boolean;
	/** Service detail carousel — prefer full `url` over listing `displayUrl`. */
	preferOriginal?: boolean;
};

export default function GalleryRasterImage({
	source,
	fallbackRasterSources = [],
	alt,
	className,
	loading = "lazy",
	listingCard = false,
	preferOriginal = false,
	onClick,
}: Props) {
	const [candidateIndex, setCandidateIndex] = useState(0);
	const [loadFailed, setLoadFailed] = useState(false);

	const candidates = useMemo(() => {
		const out =
			typeof source === "string"
				? (() => {
						if (listingCard) {
							return buildGalleryLoadCandidatesForItem({ url: source }, { listingCard: true });
						}
						const list: string[] = [];
						appendGalleryLoadCandidatesForRawUrl(list, source);
						return list;
					})()
				: buildGalleryLoadCandidatesForItem(source, { listingCard, preferOriginal });

		for (const fallback of fallbackRasterSources) {
			appendGalleryLoadCandidatesForRawUrl(out, fallback);
		}
		return out;
	}, [source, fallbackRasterSources, listingCard, preferOriginal]);

	const candidatesKey = useMemo(() => candidates.join("|"), [candidates]);

	useEffect(() => {
		setCandidateIndex(0);
		setLoadFailed(false);
	}, [candidatesKey]);

	const handleError = useCallback(() => {
		if (candidateIndex + 1 < candidates.length) {
			setCandidateIndex(candidateIndex + 1);
			return;
		}
		setLoadFailed(true);
	}, [candidateIndex, candidates.length]);

	const src = candidates[candidateIndex];
	if (!src || loadFailed) {
		return <div className={cn("size-full bg-muted/40", className)} aria-hidden />;
	}

	return (
		// eslint-disable-next-line @next/next/no-img-element
		<img
			key={src}
			src={src}
			alt={alt}
			draggable={false}
			loading={loading}
			decoding="async"
			referrerPolicy="no-referrer"
			suppressHydrationWarning
			onClick={onClick}
			onError={handleError}
			className={className}
		/>
	);
}

export { getServiceMediaRasterSource, getServiceMediaOriginalSource };
